import { Download, X } from 'lucide-react-native';
import { initialWindowMetrics, useSafeAreaInsets } from 'react-native-safe-area-context';
import { twJoin } from 'tailwind-merge';

import { PreviewActionButton } from './PreviewActionButton';
import { ZoomableImage } from './ZoomableImage';

import {
  Actionsheet,
  ActionsheetBackdrop,
  ActionsheetContent,
  ActionsheetDragIndicator,
  ActionsheetDragIndicatorWrapper,
} from '@/components/ui/actionsheet';
import { GLASS_ICON_BUTTON_SIZE_CLASS, GlassSurface, useLiquidGlass } from '@/components/ui/glass';
import { HStack } from '@/components/ui/hstack';
import { Text } from '@/components/ui/text';
import { View } from '@/components/ui/view';

/**
 * How much of the screen the sheet covers. The rest is the strip above it, which
 * keeps it reading as a sheet and clears the status bar and Dynamic Island.
 */
const SHEET_HEIGHT_PERCENT = 92;

/** The gap between the zoom buttons and the home indicator. */
const CONTROLS_GAP = 8;

type ImagePreviewSheetProps = {
  isOpen: boolean;
  uri: string;
  name: string;
  onClose: () => void;
  /**
   * Hands the file to the platform's viewer or share sheet. Omitted while the file has no stored
   * copy. The caller closes the preview first so the file opens over the thread, not over this sheet.
   */
  onDownload?: () => void;
};

/**
 * An image from a support thread, in a tall bottom sheet like the composer's
 * attach menu. The picture fills the sheet and the controls float over it.
 *
 * The overlays let touches through where they draw nothing, so the picture can
 * be dragged from anywhere. The sheet draws in the root overlay, not a native
 * modal, which is why a download can open the system share sheet as soon as
 * this closes.
 *
 * Its height is set through `snapPoints`; see the bottom sheet note in the
 * animations rule for why a style height would be dropped.
 */
export function ImagePreviewSheet({
  isOpen,
  uri,
  name,
  onClose,
  onDownload,
}: ImagePreviewSheetProps) {
  const { bottom: scopedBottom } = useSafeAreaInsets();

  /*
   * The sheet is opened from a screen inside a native tab, where the hook's
   * bottom inset includes the tab bar's height. This sheet covers the tab bar,
   * so that value would float the buttons well above the edge. The window's
   * own inset is only the home indicator. It is read once at launch, which is
   * safe because the app is portrait-only on phones, so it cannot change.
   */
  const bottom = initialWindowMetrics?.insets.bottom ?? scopedBottom;

  const hasGlass = useLiquidGlass();

  return (
    <Actionsheet isOpen={isOpen} onClose={onClose} snapPoints={[SHEET_HEIGHT_PERCENT]}>
      <ActionsheetBackdrop />

      <ActionsheetContent className="w-full overflow-hidden rounded-t-[32px] bg-background-50 p-0 md:max-w-3xl">
        <ZoomableImage uri={uri} name={name} controlsBottomInset={bottom + CONTROLS_GAP} />

        <View pointerEvents="box-none" className="w-full">
          <ActionsheetDragIndicatorWrapper>
            <ActionsheetDragIndicator />
          </ActionsheetDragIndicatorWrapper>

          <HStack pointerEvents="box-none" className="w-full items-center gap-3 px-4 pt-1">
            <PreviewActionButton label="Close image preview" icon={X} onPress={onClose} />

            <View pointerEvents="none" className="min-w-0 flex-1 items-center">
              <GlassSurface
                tone="dark"
                className="max-w-full rounded-full px-4 py-2"
                fallbackClassName="bg-background-0 shadow-soft-1">
                <Text
                  className={twJoin(
                    'font-semibold text-base',
                    hasGlass ? 'text-typography-white' : 'text-typography-900'
                  )}
                  numberOfLines={1}>
                  {name}
                </Text>
              </GlassSurface>
            </View>

            {onDownload ? (
              <PreviewActionButton label="Download" icon={Download} onPress={onDownload} />
            ) : (
              <View className={twJoin(GLASS_ICON_BUTTON_SIZE_CLASS, 'shrink-0')} aria-hidden />
            )}
          </HStack>
        </View>
      </ActionsheetContent>
    </Actionsheet>
  );
}
